import { createContext, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useCreateManualShortLinkMutation,
  useRevokeManualShortLinkMutation,
  useRevokeManyShortLinksMutation,
  useUpdateManualShortLinkMutation,
} from 'core/graphql';
import { GlobalToastTypeNames, ToastTypes } from 'types';

import { copyProducedText } from '../Esign/clipboard';
import { useToasts } from '../Toast/useToasts';
import {
  chunkShortLinkIds,
  IShortLinkError,
  shortLinkErrorKey,
  toShortLinkErrors,
} from './shortLinkModel';
import { IShortLinkRow } from './useShortLinks';

export interface IShortLinkMutationResult {
  ok: boolean;
  link: IShortLinkRow | null;
  errors: IShortLinkError[];
}

/** Итог «Погасить выбранные»: сколько погашено и сколько пропущено. */
export interface IShortLinkBulkResult {
  ok: boolean;
  revoked: number;
  skipped: number;
  errors: IShortLinkError[];
}

/** Поля ручной ссылки, которые человек задаёт в форме. */
export interface IShortLinkInput {
  title: string;
  /** Тип: код из списка, свой текст или `''` — не указан. */
  channel: string;
  tags: string[];
}

interface IPayload {
  success?: boolean | null;
  customErrors?: Parameters<typeof toShortLinkErrors>[0];
  link?: IShortLinkRow | null;
}

/**
 * Перечитать открытый список после создания, правки или погашения. Страница
 * кладёт сюда `refetch` своего списка — с его фильтрами и страницей — и
 * справочника фильтров (новый тип или метка сразу появляются в выпадашках).
 *
 * Не вытеснение полей из кэша: на Apollo 3.3 список после `cache.evict`
 * перечитывался, но таблица оставалась на старых строках (новая ссылка не
 * появлялась, пока не перезагрузишь страницу — живая проверка 0080). И не
 * `refetchQueries` по имени: так бьётся и пропущенный (`skip`) запрос.
 */
export const ShortLinkListRefreshContext = createContext<() => void>(
  () => undefined,
);

/** Отклонённый промис (сеть, права) — общей ошибкой формы. */
const crash = (error: unknown): IShortLinkMutationResult => ({
  ok: false,
  link: null,
  errors: [
    {
      field: '',
      code: '',
      message: error instanceof Error ? error.message : '',
    },
  ],
});

/**
 * ТЗ 0080 — действия над короткими ссылками.
 *
 * Мутации одной ссылки НЕ поднимают тост с ошибкой: окно создания и вложенные
 * окна правки и погашения рисуют отказ сами, у своего поля (вложенное окно
 * стоит выше уведомлений приложения, и тост ушёл бы под его затемнение).
 * Отказ сети / прав (промис отклонён) превращается в общую ошибку формы —
 * без try/catch это необработанный reject и ноль обратной связи.
 */
export const useShortLinkMutations = () => {
  const { t } = useTranslation();
  const { addToast } = useToasts();

  const refreshList = useContext(ShortLinkListRefreshContext);
  const [
    createLink,
    { loading: creating },
  ] = useCreateManualShortLinkMutation();
  const [
    updateLink,
    { loading: updating },
  ] = useUpdateManualShortLinkMutation();
  const [
    revokeLink,
    { loading: revoking },
  ] = useRevokeManualShortLinkMutation();
  const [
    revokeLinks,
    { loading: revokingMany },
  ] = useRevokeManyShortLinksMutation();

  const notify = useCallback(
    (type: ToastTypes, title: string) => {
      addToast({
        data: { type, title },
        type: GlobalToastTypeNames.ShortLinks,
      });
    },
    [addToast],
  );

  /** Текст ошибки: перевод известного кода, иначе текст сервера. */
  const errorText = useCallback(
    (error: IShortLinkError): string => {
      const key = shortLinkErrorKey(error.code);
      if (key) return t(key);
      return error.message || t('shortLinks.errors.unknown');
    },
    [t],
  );

  const settle = useCallback(
    (
      payload: IPayload | null | undefined,
      successKey: string | null,
    ): IShortLinkMutationResult => {
      const errors = toShortLinkErrors(payload?.customErrors);
      if (payload?.success && payload.link) {
        if (successKey) notify(ToastTypes.success, t(successKey));
        refreshList();
        return { ok: true, link: payload.link, errors };
      }
      return {
        ok: false,
        link: payload?.link ?? null,
        // Отказ без единой ошибки — всё равно отказ: форма обязана его
        // показать, а не молча остаться открытой.
        errors: errors.length ? errors : [{ field: '', code: '', message: '' }],
      };
    },
    [notify, refreshList, t],
  );

  /** Создать ручную ссылку. Успех показывает само окно — без тоста. */
  const create = useCallback(
    async (
      targetUrl: string,
      input: IShortLinkInput,
      expiresAt: string | null,
    ): Promise<IShortLinkMutationResult> => {
      try {
        const { data } = await createLink({
          variables: {
            targetUrl,
            title: input.title,
            channel: input.channel,
            tags: input.tags,
            expiresAt,
          },
        });
        return settle(data?.createShortLink, null);
      } catch (error) {
        return crash(error);
      }
    },
    [createLink, settle],
  );

  /**
   * Название, тип, метки и/или срок. `expiresAt = null` без `clearExpiry` —
   * срок не трогаем (у истёкшей ссылки прежний срок в прошлом, и отправить
   * его обратно значило бы получить `INVALID_EXPIRY` на простом
   * переименовании). Тип и метки уходят всегда — форма показывает их целиком.
   */
  const update = useCallback(
    async (
      id: string,
      input: IShortLinkInput,
      expiresAt: string | null,
      clearExpiry: boolean,
    ): Promise<IShortLinkMutationResult> => {
      try {
        const { data } = await updateLink({
          variables: {
            id,
            title: input.title,
            channel: input.channel,
            tags: input.tags,
            expiresAt: clearExpiry ? null : expiresAt,
            clearExpiry: clearExpiry || null,
          },
        });
        return settle(data?.updateShortLink, 'shortLinks.toast.updated');
      } catch (error) {
        return crash(error);
      }
    },
    [updateLink, settle],
  );

  /** Погасить: ссылка перестаёт открываться сразу. */
  const revoke = useCallback(
    async (id: string): Promise<IShortLinkMutationResult> => {
      try {
        const { data } = await revokeLink({ variables: { id } });
        return settle(data?.revokeShortLink, 'shortLinks.toast.revoked');
      } catch (error) {
        return crash(error);
      }
    },
    [revokeLink, settle],
  );

  /**
   * Погасить выбранные. Пачками по 100 (предел сервера); каждая ссылка — по
   * своим правам, чужие и уже погашенные сервер пропускает. Итог — тостом
   * «погашено X, пропущено Y»: окно подтверждения к этому моменту закрыто.
   * `skippedBefore` — сколько выбранных страница не отправила вовсе (гасить
   * их нельзя): они тоже «пропущены».
   */
  const revokeMany = useCallback(
    async (ids: string[], skippedBefore = 0): Promise<IShortLinkBulkResult> => {
      let revoked = 0;
      let skipped = skippedBefore;
      try {
        const chunks = chunkShortLinkIds(ids);
        for (let at = 0; at < chunks.length; at += 1) {
          // Пачки по очереди: итог «погашено / пропущено» складывается из
          // ответов, и при отказе пачки следующие не уходят.
          // eslint-disable-next-line no-await-in-loop
          const { data } = await revokeLinks({
            variables: { ids: chunks[at] },
          });
          const payload = data?.revokeShortLinks;
          const errors = toShortLinkErrors(payload?.customErrors);
          if (!payload?.success) {
            refreshList();
            return {
              ok: false,
              revoked,
              skipped,
              errors: errors.length
                ? errors
                : [{ field: '', code: '', message: '' }],
            };
          }
          revoked += payload.revokedIds.length;
          skipped += payload.skipped;
        }
      } catch (error) {
        refreshList();
        return { ok: false, revoked, skipped, errors: crash(error).errors };
      }
      refreshList();
      notify(
        revoked ? ToastTypes.success : ToastTypes.error,
        t('shortLinks.bulk.done', { revoked, skipped }),
      );
      return { ok: true, revoked, skipped, errors: [] };
    },
    [notify, refreshList, revokeLinks, t],
  );

  /**
   * Скопировать короткую ссылку. Ручная ссылка — не секрет (её и так видно
   * на экране), поэтому без журнала и без запроса к серверу.
   */
  const copyUrl = useCallback(
    async (url: string): Promise<boolean> => {
      const outcome = await copyProducedText(async () => url || null);
      if (outcome === 'copied') {
        notify(ToastTypes.success, t('shortLinks.toast.copied'));
        return true;
      }
      notify(ToastTypes.error, t('shortLinks.toast.copyFailed'));
      return false;
    },
    [notify, t],
  );

  return {
    create,
    update,
    revoke,
    revokeMany,
    copyUrl,
    errorText,
    busy: { creating, updating, revoking, revokingMany },
  };
};
